import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, fmtDate, API } from "@/lib/api";
import { Bell, Plus, Loader2, Calendar, MapPin, PartyPopper, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, Upload, Eye, Clock, ArrowDownUp, Copy, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const CAT_LABELS = { news: "Actualité", event: "Événement", exam: "Examen", meeting: "Réunion", communique: "Communiqué" };
const CAT_COLORS = {
  news: "text-[#166534] bg-[#166534]/10",
  event: "text-[#EA580C] bg-[#EA580C]/10",
  exam: "text-red-600 bg-red-50",
  meeting: "text-[#1E3A8A] bg-[#1E3A8A]/10",
  communique: "text-[#FBBF24] bg-[#FBBF24]/10",
};

const SORT_OPTIONS = {
  created_desc: "Plus récentes d'abord",
  created_asc: "Plus anciennes d'abord",
  title_asc: "Titre (A → Z)",
  title_desc: "Titre (Z → A)",
  category_asc: "Par catégorie",
  views_desc: "Plus vues d'abord",
};

const PAGE_SIZE = 10;
const emptyForm = { title: "", content: "", category: "news", event_date: "", event_location: "", image_url: "", publish_at: "" };

const toLocalDatetime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

// Resolve image URL (if it starts with /api/, prefix with backend)
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const fileInputRef = useRef(null);

  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_desc");
  const [page, setPage] = useState(1);

  const load = () => {
    // Backend sort for date/title/category, frontend sort for views_desc
    const backendSort = sortBy === "views_desc" ? "created_desc" : sortBy;
    api.get(`/announcements?sort=${backendSort}`).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [sortBy]);

  const canCreate = ["admin", "super_admin", "founder"].includes(user?.role);

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title || "",
      content: a.content || "",
      category: a.category || "news",
      event_date: a.event_date ? toLocalDatetime(a.event_date) : "",
      event_location: a.event_location || "",
      image_url: a.image_url || "",
      publish_at: a.publish_at ? toLocalDatetime(a.publish_at) : "",
    });
    setOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({...f, image_url: data.url}));
      toast.success("Image téléversée !");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const submit = async () => {
    if (!form.title || !form.content) return toast.error("Titre et contenu requis");
    if (form.category === "event" && !form.event_date) {
      return toast.error("Veuillez choisir une date pour l'événement");
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        content: form.content,
        category: form.category,
        image_url: form.image_url || "",
      };
      if (form.category === "event") {
        payload.event_date = new Date(form.event_date).toISOString();
        payload.event_location = form.event_location || "";
      } else {
        payload.event_date = null;
        payload.event_location = "";
      }
      payload.publish_at = form.publish_at ? new Date(form.publish_at).toISOString() : null;

      if (editingId) {
        await api.put(`/announcements/${editingId}`, payload);
        toast.success("Annonce modifiée");
      } else {
        const willBeScheduled = payload.publish_at && new Date(payload.publish_at) > new Date();
        await api.post("/announcements", payload);
        toast.success(willBeScheduled ? `Annonce programmée pour le ${new Date(payload.publish_at).toLocaleString("fr-FR")}` : (form.category === "event" ? "Événement publié !" : "Annonce publiée"));
      }
      setOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleDelete = async (id, title) => {
    try {
      await api.delete(`/announcements/${id}`);
      toast.success(`"${title}" supprimée`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await api.post(`/announcements/${id}/duplicate`);
      toast.success(`"${data.title}" créée — modifiez-la maintenant.`);
      load();
      // Auto-open edit on the new copy
      openEdit(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la duplication");
    }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (tab !== "all") list = list.filter(a => a.category === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(a => (a.title || "").toLowerCase().includes(q) || (a.content || "").toLowerCase().includes(q));
    }
    if (sortBy === "views_desc") {
      list = [...list].sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    return list;
  }, [items, tab, search, sortBy]);

  useEffect(() => { setPage(1); }, [tab, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const now = new Date();
  const upcomingEvents = items
    .filter(a => a.category === "event" && a.event_date && new Date(a.event_date) > now && (!a.publish_at || new Date(a.publish_at) <= now))
    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  const nextEventId = upcomingEvents[0]?.id;

  const tabsConfig = [
    { v: "all", label: "Toutes", count: items.length },
    { v: "news", label: "Actualités", count: items.filter(a => a.category === "news").length },
    { v: "event", label: "Événements", count: items.filter(a => a.category === "event").length },
    { v: "exam", label: "Examens", count: items.filter(a => a.category === "exam").length },
    { v: "meeting", label: "Réunions", count: items.filter(a => a.category === "meeting").length },
    { v: "communique", label: "Communiqués", count: items.filter(a => a.category === "communique").length },
  ];

  const totalViews = items.reduce((sum, a) => sum + (a.views || 0), 0);

  return (
    <div className="space-y-5" data-testid="page-announcements">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] tracking-tight">Annonces & événements</h2>
          <p className="text-sm text-slate-500 mt-1">
            {items.length} publication{items.length > 1 ? "s" : ""} · <Eye className="inline h-3.5 w-3.5" /> {totalViews} vues totales
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-announcement-btn">
            <Plus className="h-4 w-4" /> Publier
          </Button>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyForm); setEditingId(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Modifier la publication" : "Nouvelle publication"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Catégorie *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                <SelectTrigger data-testid="annc-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CAT_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Titre *</Label>
              <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Titre de la publication" data-testid="annc-title" />
            </div>

            {form.category === "event" && (
              <>
                <div>
                  <Label>Date et heure de l&apos;événement *</Label>
                  <Input type="datetime-local" value={form.event_date} onChange={(e) => setForm({...form, event_date: e.target.value})} data-testid="annc-event-date" />
                </div>
                <div>
                  <Label>Lieu</Label>
                  <Input value={form.event_location} onChange={(e) => setForm({...form, event_location: e.target.value})} placeholder="Ex: Site principal" data-testid="annc-event-location" />
                </div>
              </>
            )}

            {/* Image upload */}
            <div>
              <Label className="flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Image d&apos;illustration</Label>
              <div className="flex gap-2 mt-1">
                <Input type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={fileInputRef} onChange={handleFileUpload} disabled={uploading} className="flex-1" data-testid="annc-image-file" />
                {uploading && <Loader2 className="h-5 w-5 animate-spin text-[#166534] self-center" />}
              </div>
              <p className="text-xs text-slate-500 mt-1">JPG, PNG, WebP, GIF (max 5 Mo). Ou collez une URL ci-dessous.</p>
              <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://..." className="mt-2" data-testid="annc-image-url" />
              {form.image_url && (
                <div className="mt-2 relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-video">
                  <img src={resolveImg(form.image_url)} alt="Aperçu" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} data-testid="annc-image-preview" />
                  <button type="button" onClick={() => setForm({...form, image_url: ""})} className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 hover:bg-white shadow" data-testid="annc-image-remove">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Schedule publishing */}
            <div>
              <Label className="flex items-center gap-2"><Clock className="h-4 w-4" /> Programmer la publication (optionnel)</Label>
              <Input type="datetime-local" value={form.publish_at} onChange={(e) => setForm({...form, publish_at: e.target.value})} data-testid="annc-publish-at" />
              <p className="text-xs text-slate-500 mt-1">Laissez vide pour publier immédiatement. Si une date future est indiquée, l&apos;annonce restera invisible sur le site jusqu&apos;à ce moment.</p>
            </div>

            <div>
              <Label>Description *</Label>
              <Textarea rows={5} value={form.content} onChange={(e) => setForm({...form, content: e.target.value})} data-testid="annc-content" />
            </div>

            <Button onClick={submit} disabled={loading} className="bg-[#166534] hover:bg-[#14532d] text-white w-full" data-testid="annc-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingId ? "Enregistrer les modifications" : "Publier")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Next event banner */}
      {upcomingEvents.length > 0 && (
        <div className="bg-gradient-to-r from-[#EA580C]/10 to-[#FBBF24]/10 border border-[#EA580C]/20 rounded-xl p-5 flex gap-4 items-center" data-testid="upcoming-events-banner">
          <div className="w-12 h-12 rounded-xl bg-[#EA580C] text-white flex items-center justify-center flex-shrink-0"><PartyPopper className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#1E3A8A]">{upcomingEvents[0].title}</div>
            <div className="text-xs text-slate-500 mt-0.5">Affiché en haut de la page d&apos;accueil · {fmtDate(upcomingEvents[0].event_date)}</div>
          </div>
        </div>
      )}

      {/* Filters: Tabs + Search + Sort */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-100 flex flex-wrap h-auto" data-testid="annc-tabs">
            {tabsConfig.map((t) => (
              <TabsTrigger key={t.v} value={t.v} className="data-[state=active]:bg-white data-[state=active]:text-[#1E3A8A] data-[state=active]:shadow-sm gap-2" data-testid={`annc-tab-${t.v}`}>
                {t.label}
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200/60 text-slate-600 font-mono">{t.count}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher titre ou contenu..." className="pl-9" data-testid="annc-search" />
          </div>
          <div className="sm:w-64">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger data-testid="annc-sort"><ArrowDownUp className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SORT_OPTIONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {pageItems.map((a, i) => {
          const colorClass = CAT_COLORS[a.category] || CAT_COLORS.news;
          const isNextEvent = a.id === nextEventId;
          const isEvent = a.category === "event" && a.event_date;
          const isScheduled = a.publish_at && new Date(a.publish_at) > new Date();
          return (
            <div key={a.id} className={`bg-white border rounded-xl overflow-hidden ${isNextEvent ? "border-[#EA580C] ring-2 ring-[#EA580C]/10" : "border-slate-200"} ${isScheduled ? "opacity-75" : ""}`} data-testid={`annc-item-${i}`}>
              <div className="flex gap-4 p-5">
                {a.image_url ? (
                  <img src={resolveImg(a.image_url)} alt="" className="w-24 h-24 lg:w-32 lg:h-32 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center flex-shrink-0`}>
                    {a.category === "event" ? <PartyPopper className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="chip">{CAT_LABELS[a.category]}</span>
                    {isNextEvent && <span className="chip bg-[#EA580C]/10 border-[#EA580C]/20 text-[#EA580C]"><PartyPopper className="h-3 w-3" /> Affiché en accueil</span>}
                    {isScheduled && <span className="chip bg-blue-50 border-blue-200 text-blue-700"><Clock className="h-3 w-3" /> Programmée · {fmtDate(a.publish_at)}</span>}
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500 ml-auto"><Eye className="h-3 w-3" /> {a.views || 0}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#1E3A8A] leading-snug mb-1">{a.title}</h3>
                  <div className="text-xs text-slate-500 mb-2">Publié le {fmtDate(a.created_at)}{a.updated_at ? ` · Modifié le ${fmtDate(a.updated_at)}` : ""}</div>
                  {isEvent && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2 text-xs text-slate-600">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5 text-[#EA580C]" /> {fmtDate(a.event_date)}</span>
                      {a.event_location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-[#EA580C]" /> {a.event_location}</span>}
                    </div>
                  )}
                  <p className="text-sm text-slate-700 leading-relaxed line-clamp-3">{a.content}</p>
                </div>
                {canCreate && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} className="gap-1 text-xs" data-testid={`annc-edit-${i}`}>
                      <Pencil className="h-3 w-3" /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDuplicate(a.id)} className="gap-1 text-xs border-blue-200 text-blue-700 hover:bg-blue-50" data-testid={`annc-duplicate-${i}`}>
                      <Copy className="h-3 w-3" /> Dupliquer
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="gap-1 text-xs border-red-200 text-red-700 hover:bg-red-50 hover:text-red-700" data-testid={`annc-delete-${i}`}>
                          <Trash2 className="h-3 w-3" /> Supprimer
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Supprimer cette publication ?</AlertDialogTitle>
                          <AlertDialogDescription>&quot;{a.title}&quot; sera définitivement supprimée. Action irréversible.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid={`annc-delete-cancel-${i}`}>Annuler</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(a.id, a.title)} className="bg-red-600 hover:bg-red-700" data-testid={`annc-delete-confirm-${i}`}>
                            Oui, supprimer
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center text-slate-500 py-16 bg-white rounded-xl border border-slate-200">
            {search ? `Aucun résultat pour "${search}"` : "Aucune annonce dans cette catégorie"}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-5 py-3" data-testid="pagination">
          <div className="text-sm text-slate-600">Page <strong>{safePage}</strong> sur <strong>{totalPages}</strong> · {filtered.length} résultats</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1} className="gap-1" data-testid="page-prev"><ChevronLeft className="h-4 w-4" /> Préc.</Button>
            <Button size="sm" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="gap-1" data-testid="page-next">Suiv. <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
